import { useState } from "react";
import * as VscIcons from "react-icons/vsc";
import { FiLogOut, FiMenu, FiSettings, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, NavLink, useLocation, useNavigate } from "react-router-dom";

import { sidebarLinks } from "../../../data/dashboard-links";
import { logout } from "../../../services/operations/authAPI";
import { resetCourseState } from "../../../slices/courseSlice";
import ConfirmationModal from "../../common/ConfirmationModal";

/* ── Shared active-link class builder ─────────────────────────── */
const linkCls = (active) =>
  [
    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium",
    "border-l-2 transition-all duration-150 select-none",
    active
      ? "border-yellow-50 bg-yellow-50/10 text-yellow-50"
      : "border-transparent text-richblack-300 hover:bg-richblack-700/60 hover:text-richblack-5",
  ].join(" ");

/* ── Sidebar content (shared between desktop + mobile) ─────────── */
function SidebarInner({ user, filteredLinks, isActive, onLinkClick, onLogout }) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* User info */}
      <div className="flex items-center gap-3 border-b border-richblack-700 px-5 py-4">
        <img
          src={
            user?.image ||
            `https://api.dicebear.com/5.x/initials/svg?seed=${user?.firstName}`
          }
          alt={user?.firstName}
          className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-richblack-700"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-richblack-5">
            {user?.firstName} {user?.lastName}
          </p>
          <p className="truncate text-[11px] text-richblack-400">{user?.email}</p>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
        {filteredLinks.map((link) => {
          const Icon = VscIcons[link.icon];
          return (
            <NavLink
              key={link.id}
              to={link.path}
              onClick={onLinkClick}
              className={linkCls(isActive(link.path))}
            >
              {Icon && <Icon className="flex-shrink-0 text-[15px]" />}
              {link.name}
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom: Settings + Logout */}
      <div className="flex flex-col gap-1 border-t border-richblack-700 px-3 py-4">
        <NavLink
          to="/dashboard/settings"
          onClick={onLinkClick}
          className={linkCls(isActive("/dashboard/settings"))}
        >
          <FiSettings className="flex-shrink-0 text-[15px]" />
          Settings
        </NavLink>

        <button
          onClick={onLogout}
          className={`${linkCls(false)} w-full text-left`}
        >
          <FiLogOut className="flex-shrink-0 text-[15px]" />
          Logout
        </button>
      </div>
    </div>
  );
}

/* ── Main export ───────────────────────────────────────────────── */
export default function Sidebar2() {
  const { user } = useSelector((s) => s.profile);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modal, setModal]           = useState(null);

  const isActive = (path) => !!matchPath({ path }, location.pathname);

  const filteredLinks = sidebarLinks.filter(
    (l) => !l.type || user?.accountType === l.type
  );

  const handleLinkClick = () => {
    dispatch(resetCourseState());
    setMobileOpen(false);
  };

  const openLogoutModal = () =>
    setModal({
      text1: "Are you sure?",
      text2: "You will be logged out of your account.",
      btn1Text: "Logout",
      btn2Text: "Cancel",
      btn1Handler: () => dispatch(logout(navigate)),
      btn2Handler: () => setModal(null),
    });

  const sharedProps = { user, filteredLinks, isActive, onLinkClick: handleLinkClick, onLogout: openLogoutModal };

  return (
    <>
      {/* ── Mobile FAB ─────────────────────────────────────────── */}
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation"
        className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-50 text-richblack-900 shadow-[0_4px_20px_rgba(0,0,0,0.4)] md:hidden"
      >
        <FiMenu size={20} />
      </button>

      {/* ── Mobile backdrop ────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-richblack-900/70 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Mobile drawer ──────────────────────────────────────── */}
      <aside
        className={`fixed left-0 top-[60px] z-50 flex h-[calc(100vh-60px)] w-64 flex-col
          border-r border-richblack-700 bg-richblack-800
          transition-transform duration-300 ease-in-out
          md:hidden
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Close row */}
        <div className="flex items-center justify-between border-b border-richblack-700 px-4 py-3">
          <span className="text-sm font-semibold text-richblack-5">Menu</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded-lg p-1.5 text-richblack-400 hover:bg-richblack-700 hover:text-richblack-5 transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>
        <SidebarInner {...sharedProps} />
      </aside>

      {/* ── Desktop sidebar (always visible, sticky) ───────────── */}
      <aside className="sticky top-[60px] hidden h-[calc(100vh-60px)] w-60 flex-shrink-0 flex-col border-r border-richblack-700 bg-richblack-800 md:flex">
        <SidebarInner {...sharedProps} />
      </aside>

      {modal && <ConfirmationModal modalData={modal} />}
    </>
  );
}
